import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sparkle, WarningCircle, ArrowRight, Info, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAccessGate } from "@/hooks/useAccessGate";

const REFINE_EXAMPLES = [
  "Raccourcis le questionnaire à 5 questions essentielles.",
  "Ajoute 2 questions sur les antécédents familiaux.",
  "Convertis les questions en échelle Likert à 5 niveaux.",
  "Reformule les questions pour un public adolescent (12-16 ans).",
];

/**
 * Refinement dialog for the TemplateBuilder page.
 *
 * Sends the current draft state + a refinement prompt to Claude via
 * `POST /api/ai/templates/generate`. On success, `onApply(draft)` is invoked
 * so the parent can merge the returned questionnaire into its form state.
 * Nothing is persisted server-side here — the practitioner reviews then Saves.
 */
export default function AIRefineDialog({ open, onOpenChange, currentTpl, onApply }) {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const { isTrialExpired, gateProps } = useAccessGate();

  const submit = async (e) => {
    e.preventDefault();
    const p = prompt.trim();
    if (p.length < 8) {
      toast.error("Précisez ce que vous voulez affiner (8 caractères minimum)");
      return;
    }
    if (!currentTpl || !(currentTpl.questions || []).length) {
      toast.error("Ajoutez au moins une question avant d'affiner avec l'IA");
      return;
    }
    setLoading(true);
    try {
      // Send only the "TemplateIn-shaped" fields — strip scoring artifacts
      // that shouldn't be persisted (empty dimensions, incomplete bands).
      const scoring = currentTpl.scoring && currentTpl.scoring.type !== "none"
        ? {
          type: currentTpl.scoring.type,
          interpretation: (currentTpl.scoring.interpretation || []).filter(
            (b) => b && b.label && b.label.trim()
          ),
        }
        : null;
      const current = {
        title: currentTpl.title || "",
        description: currentTpl.description || "",
        category: currentTpl.category || "",
        instructions: currentTpl.instructions || "",
        questions: currentTpl.questions || [],
        scoring,
      };
      const { data: draft } = await api.post("/ai/templates/generate", {
        prompt: p,
        current,
      });
      onApply(draft);
      toast.success("Brouillon affiné — vérifiez puis enregistrez");
      onOpenChange(false);
      setPrompt("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "L'affinage a échoué";
      toast.error(String(msg).slice(0, 200));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!loading) {
          onOpenChange(v);
          if (!v) setPrompt("");
        }
      }}
    >
      <DialogContent
        className="sm:max-w-[560px] bg-white border-[#E1E9F0]"
        data-testid="ai-refine-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[#1E3A5F] flex items-center gap-2">
            <Sparkle size={20} weight="duotone" className="text-[#3A8FA5]" />
            Régénérer avec l'IA
          </DialogTitle>
          <DialogDescription className="text-[#5F758F]">
            Décrivez ce que vous souhaitez modifier. L'IA affine le questionnaire
            actuel — vous pouvez ensuite éditer chaque question avant d'enregistrer.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {isTrialExpired && (
            <div className="rounded-[10px] bg-[#FFF3D6] border border-[#F0DDA8] px-3 py-2.5 flex items-start gap-2" data-testid="ai-refine-trial-expired">
              <Lock size={14} weight="duotone" className="text-[#8B2A1E] shrink-0 mt-0.5" />
              <div className="text-[12px] text-[#7A4C0B] leading-relaxed">
                <p className="font-semibold text-[#8B2A1E] mb-0.5">Période d'essai terminée</p>
                Souscrivez pour affiner ce questionnaire avec l'IA.
              </div>
            </div>
          )}
          <div>
            <Label
              htmlFor="ai-refine-prompt"
              className="text-[13px] font-semibold text-[#1E3A5F]"
            >
              Quelle modification souhaitez-vous ?
            </Label>
            <Textarea
              id="ai-refine-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Ex : Raccourcis à 6 questions, garde uniquement les items sur l'humeur et le sommeil, et ajoute un score global sur 24 avec 4 niveaux d'interprétation."
              rows={6}
              maxLength={4000}
              disabled={loading}
              className="mt-1.5 bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5] text-sm"
              data-testid="ai-refine-prompt"
              autoFocus
            />
            <div className="mt-1 text-[11px] text-[#5F758F] text-right">
              {prompt.length} / 4000
            </div>
          </div>

          <div className="bg-[#F5F9FB] border border-[#E1E9F0] rounded-[10px] p-3">
            <p className="text-[11px] tracking-[0.1em] uppercase font-semibold text-[#5F758F] mb-2 flex items-center gap-1.5">
              <Info size={12} weight="duotone" /> Idées d'affinage
            </p>
            <ul className="space-y-1.5">
              {REFINE_EXAMPLES.map((ex, i) => (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => setPrompt(ex)}
                    disabled={loading}
                    className="text-left text-[12px] text-[#3A8FA5] hover:text-[#1E3A5F] hover:underline disabled:opacity-50"
                    data-testid={`ai-refine-example-${i}`}
                  >
                    « {ex} »
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-start gap-2 bg-[#FFF7ED] border border-[#F0C88F] rounded-[10px] p-3">
            <WarningCircle
              size={16}
              weight="duotone"
              className="text-[#C97A2E] flex-shrink-0 mt-0.5"
            />
            <p className="text-[12px] text-[#7A5220] leading-relaxed">
              Les modifications proposées <strong>remplacent le brouillon actuel</strong>.
              Pensez à enregistrer après avoir vérifié chaque question.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="rounded-[10px] border-[#E1E9F0] text-[#5F758F]"
              data-testid="ai-refine-cancel"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading || prompt.trim().length < 8 || isTrialExpired}
              className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="ai-refine-submit"
              {...(isTrialExpired ? gateProps({ title: "Régénérer" }) : {})}
            >
              {loading ? (
                <>
                  <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin mr-2" />
                  Affinage en cours…
                </>
              ) : (
                <>
                  <Sparkle size={16} weight="duotone" className="mr-2" /> Régénérer
                  <ArrowRight size={14} className="ml-2" />
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
